import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Check, Copy, Inbox, Circle, CheckCircle2 } from "lucide-react";
import { fdate, short } from "@/lib/api";

export const STATUS_STYLE = {
  recibido: "bg-secondary text-foreground border-border",
  validado: "bg-sky-50 text-sky-800 border-sky-200",
  asignado: "bg-indigo-50 text-indigo-800 border-indigo-200",
  en_proceso: "bg-amber-50 text-amber-900 border-amber-200",
  resuelto: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cerrado: "bg-slate-100 text-slate-700 border-slate-200",
  rechazado: "bg-rose-50 text-rose-800 border-rose-200",
  pendiente: "bg-secondary text-foreground border-border",
  bloqueada: "bg-rose-50 text-rose-800 border-rose-200",
  completada: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pago_pendiente: "bg-amber-50 text-amber-900 border-amber-200",
  en_revision: "bg-sky-50 text-sky-800 border-sky-200",
  emitido: "bg-emerald-50 text-emerald-800 border-emerald-200",
  iniciado: "bg-secondary text-foreground border-border",
  emitida: "bg-amber-50 text-amber-900 border-amber-200",
  pagada: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pagado: "bg-emerald-50 text-emerald-800 border-emerald-200",
  fallido: "bg-rose-50 text-rose-800 border-rose-200",
  activa: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelada: "bg-slate-100 text-slate-700 border-slate-200",
  vigente: "bg-emerald-50 text-emerald-800 border-emerald-200",
  alta: "bg-rose-50 text-rose-800 border-rose-200",
  media: "bg-amber-50 text-amber-900 border-amber-200",
  baja: "bg-slate-100 text-slate-700 border-slate-200",
};

export const STATUS_TEXT = {
  recibido: "Recibido", validado: "Validado", asignado: "Asignado", en_proceso: "En proceso", resuelto: "Resuelto",
  cerrado: "Cerrado", rechazado: "Rechazado", pendiente: "Pendiente", bloqueada: "Bloqueada", completada: "Completada",
  pago_pendiente: "Pago pendiente", en_revision: "En revisión", emitido: "Emitido", iniciado: "Iniciado",
  emitida: "Por pagar", pagada: "Pagada", pagado: "Pagado", fallido: "Rechazado", activa: "Activa", cancelada: "Cancelada",
  vigente: "Vigente", alta: "Alta", media: "Media", baja: "Baja",
};

export const PIN_COLOR = {
  recibido: "#64748b", validado: "#2B5AA6", asignado: "#4f46e5", en_proceso: "#C77700",
  resuelto: "#1F6B4A", cerrado: "#94a3b8", rechazado: "#D64545",
};

export function StatusBadge({ status, testid }) {
  return (
    <Badge variant="outline" data-testid={testid || `status-badge-${status}`}
      className={`font-medium whitespace-nowrap ${STATUS_STYLE[status] || "bg-secondary"}`}>
      {STATUS_TEXT[status] || status}
    </Badge>
  );
}

export const DemoBadge = () => (
  <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] tracking-wide" data-testid="demo-badge">DEMO</Badge>
);

export const SimBadge = ({ label = "SIMULADO" }) => (
  <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-200 text-[10px] tracking-wide" data-testid="simulated-badge">{label}</Badge>
);

export function CopyText({ value, display, testid, className = "" }) {
  const [done, setDone] = useState(false);
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-xs ${className}`}>
      <span className="truncate" title={value}>{display || value}</span>
      <button type="button" data-testid={testid || "copy-button"} aria-label="Copiar"
        className="p-1 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard?.writeText(value);
          setDone(true);
          toast.success("Copiado al portapapeles");
          setTimeout(() => setDone(false), 1200);
        }}>
        {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

export const Hash = ({ value, n = 10, testid }) => <CopyText value={value || ""} display={short(value, n)} testid={testid} />;

export function PageHeader({ title, subtitle, actions, eyebrow }) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-6 fade-up">
      <div>
        {eyebrow && <div className="text-xs font-medium uppercase tracking-[0.14em] text-primary mb-1">{eyebrow}</div>}
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm md:text-base text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Kpi({ label, value, hint, icon: Icon, testid, tone = "primary" }) {
  const tones = { primary: "bg-[#DFF3F6] text-[#006A7C]", warn: "bg-amber-50 text-amber-800", ok: "bg-emerald-50 text-emerald-800", danger: "bg-rose-50 text-rose-700" };
  return (
    <Card className="card-hover fade-up">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm text-muted-foreground">{label}</div>
          {Icon && <span className={`rounded-lg p-2 ${tones[tone]}`}><Icon className="h-4 w-4" /></span>}
        </div>
        <div className="text-2xl md:text-3xl font-semibold mt-2 font-display" data-testid={testid}>{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

export function Empty({ title, hint, action, icon: Icon = Inbox }) {
  return (
    <div className="flex flex-col items-center text-center py-12 px-4 gap-2" data-testid="empty-state">
      <span className="rounded-full bg-secondary p-3"><Icon className="h-6 w-6 text-muted-foreground" /></span>
      <div className="font-medium">{title}</div>
      {hint && <div className="text-sm text-muted-foreground max-w-sm">{hint}</div>}
      {action}
    </div>
  );
}

export const Loading = ({ rows = 3 }) => (
  <div className="space-y-3" data-testid="loading-state">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
  </div>
);

export function Timeline({ items = [], labels = STATUS_TEXT }) {
  return (
    <ol className="relative border-l-2 border-border ml-3 space-y-5" data-testid="status-timeline">
      {items.map((t, i) => {
        const last = i === items.length - 1;
        return (
          <li key={i} className="ml-5 fade-up">
            <span className={`absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-background ${last ? "bg-primary text-white" : "bg-[#DFF3F6] text-primary"}`}>
              {last ? <Circle className="h-2 w-2 fill-current" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            </span>
            <div className={`rounded-lg p-3 ${last ? "bg-accent" : ""}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{labels[t.status] || t.status}</span>
                <span className="text-xs text-muted-foreground font-mono">{fdate(t.at)}</span>
              </div>
              {t.note && <p className="text-sm text-muted-foreground mt-0.5">{t.note}</p>}
              {t.by && <p className="text-xs text-muted-foreground mt-0.5">Por: {t.by}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function LegalPanel({ data_hash, nom151, anchor, signature, signatureValid }) {
  return (
    <div className="space-y-3 text-sm" data-testid="legal-panel">
      <Row label="Hash SHA-256"><Hash value={data_hash} testid="legal-hash-copy" /></Row>
      {signature && (
        <Row label="Firma digital">
          <span className="flex items-center gap-2">
            <span className="text-xs">{signature.algorithm}</span>
            {signatureValid !== undefined && (signatureValid
              ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Válida</Badge>
              : <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200">No válida</Badge>)}
          </span>
        </Row>
      )}
      {nom151 && (
        <Row label="Sello NOM-151">
          <span className="flex items-center gap-2"><CopyText value={nom151.constancia_id} />{nom151.simulated && <SimBadge />}</span>
        </Row>
      )}
      {anchor && (
        <Row label={`Anclaje ${anchor.network}`}>
          <span className="flex items-center gap-2"><Hash value={anchor.tx_hash} n={8} testid="legal-tx-copy" />{anchor.simulated && <SimBadge />}</span>
        </Row>
      )}
      {anchor && <Row label="Bloque"><span className="font-mono text-xs">#{anchor.block_number}</span></Row>}
    </div>
  );
}

export const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 border-b border-dashed border-border pb-2 last:border-0">
    <span className="text-muted-foreground text-xs shrink-0">{label}</span>
    <span className="min-w-0 text-right">{children}</span>
  </div>
);

export function LiveIndicator({ lastSync }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs text-muted-foreground" data-testid="live-indicator">
      <span className="live-dot h-2 w-2 rounded-full bg-[#1F6B4A]" />
      En vivo{lastSync ? ` · ${lastSync.toLocaleTimeString("es-MX")}` : ""}
    </span>
  );
}

export const PrimaryBtn = (props) => <Button {...props} className={`btn-press ${props.className || ""}`} />;
